import { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { CHAT_API_URL } from "./config";
import { supabase } from "./supabaseClient";

// Paleta Universidad Externado de Colombia. Los cinco tonos de dimension
// pasan separacion CVD, piso de vision normal y contraste sobre #0A4630.
const P = {
  bg: "#00301B", bgCard: "#0A4630", bgCard2: "#0F5A3E",
  blue: "#3F8FE0", cyan: "#3FD2FF", white: "#FFFFFF",
  gray: "#8FAFA1", grayLight: "#D3E3DA", grayDark: "#1C6047",
  gold: "#EFB447", red: "#F2705A", green: "#4FBF8F", purple: "#3F8FE0",
};

const DIMENSIONS = [
  { id: "memoria",   label: "Memoria del huésped", color: "#3FD2FF", icon: "◈" },
  { id: "canal",     label: "Canal directo",        color: "#3F8FE0", icon: "⬡" },
  { id: "ingreso",   label: "Ingreso auxiliar",     color: "#4FBF8F", icon: "◈" },
  { id: "operacion", label: "Operación y respuesta",color: "#EFB447", icon: "⬡" },
  { id: "datos",     label: "Datos y sistemas",     color: "#F2705A", icon: "◈" },
];

const EMPRESA_TIPOS = [
  { id: "independiente", label: "Hotel independiente" },
  { id: "hostal",        label: "Hostal o apartahotel" },
  { id: "cadena",        label: "Cadena o grupo hotelero" },
];

// La API key está en el backend o en Supabase; el frontend solo llama al endpoint configurado
async function callAI(messages, system) {
  try {
    const res = await fetch(CHAT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages, system }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error al conectar con el servidor");
    return data.text || "";
  } catch (error) {
    console.error("Error calling API:", error);
    return "Lo siento, ha ocurrido un error. Por favor intenta nuevamente.";
  }
}

// FIX 2: Prompt con contador explícito y trigger preciso
const SYSTEM_PROMPT = `Eres consultor de hotelería para hoteles pequeños y medianos de Colombia.

Tu tesis: un hotel que no recuerda a sus huéspedes solo puede competir por precio.

PROTOCOLO DE 6 PREGUNTAS — SIGUE EXACTAMENTE:

TURNO 1 (usuario se presenta): Saluda en una línea y haz la Pregunta 1/6.
TURNO 2: Haz la Pregunta 2/6.
TURNO 3: Haz la Pregunta 3/6.
TURNO 4: Haz la Pregunta 4/6.
TURNO 5: Haz la Pregunta 5/6.
TURNO 6: Haz la Pregunta 6/6.
TURNO 7 (usuario responde la 6): Genera SOLO el JSON del diagnóstico. CERO texto antes o después.

LAS 6 PREGUNTAS (exactamente en este orden, una sola por turno):
1/6: Memoria — Cuando un huésped que ya se hospedó antes vuelve a reservar, ¿alguien en el hotel se entera de que ya estuvo? ¿Dónde queda registrado lo que pidió la vez pasada?
2/6: Canal — ¿Cuántas de sus reservas entran por agencias en línea y cuántas directas? ¿Lo sabe de memoria o le toca consultarlo?
3/6: Ingreso auxiliar — Además de la habitación, ¿qué le venden al huésped? Late checkout, parqueadero, restaurante, tours. ¿Se ofrece siempre o solo si el huésped pregunta?
4/6: Operación — ¿Por dónde le escriben los huéspedes, cuánto se demoran ustedes en responder, y queda algo escrito de esas conversaciones?
5/6: Sistemas — ¿Qué usan para gestionar el hotel? PMS, channel manager, Excel, cuadernos. ¿Están conectados entre sí?
6/6: Decisión — ¿Quién decide en tecnología, hay presupuesto asignado este año, y qué tareas del día a día siguen siendo completamente manuales?

FORMATO JSON OBLIGATORIO (responde SOLO esto después de la respuesta 6, sin texto extra):
{"diagnostico_completo":true,"empresa":"NOMBRE","tipo":"TIPO","scores":{"memoria":0,"canal":0,"ingreso":0,"operacion":0,"datos":0},"score_global":0,"nivel":"Memoria dispersa","resumen_ejecutivo":"TEXTO 2-3 oraciones","alertas_urgentes":[{"titulo":"TITULO","descripcion":"DESCRIPCION","impacto":"Crítico"},{"titulo":"TITULO","descripcion":"DESCRIPCION","impacto":"Alto"}],"fortalezas":["TEXTO","TEXTO","TEXTO"],"roadmap":[{"fase":"Hoy mismo","acciones":["ACCION","ACCION"],"color":"red"},{"fase":"En 30 días","acciones":["ACCION","ACCION"],"color":"gold"},{"fase":"En 3 meses","acciones":["ACCION","ACCION"],"color":"blue"},{"fase":"En 6 meses","acciones":["ACCION","ACCION"],"color":"green"}],"recomendacion_ia":"TEXTO detallado"}

SCORING, y sé severo. La mayoría de los hoteles pequeños está entre 20 y 45:
- Nada queda registrado, todo en la cabeza de la gente: 10-25
- Cuadernos y Excel sueltos, sin conexión: 25-40
- PMS básico que nadie consulta antes de la llegada: 40-58
- PMS usado, algo de canal directo, upselling ocasional: 58-72
- Sistemas conectados y equipo que se adelanta al huésped: 72-88
- El hotel reconoce y anticipa sin que nadie lo pida: 88-98

REGLAS DE CONTENIDO:
- Las acciones de "Hoy mismo" no pueden costar dinero ni requerir proveedor. Un hotel de 20 habitaciones tiene que poder hacerlas esta semana.
- No menciones marcas de software en el roadmap. Habla de categorías.
- Las alertas apuntan a plata que se está perdiendo, no a buenas prácticas genéricas.
- Escribe en español de Colombia, directo y sin relleno.

nivel debe ser exactamente uno de estos: "Sin memoria", "Memoria dispersa", "Memoria parcial", "Memoria operativa", "Memoria activa"`;

// FIX 3: parseResult robusto — 3 estrategias de extracción
function parseResult(text) {
  if (!text) return null;
  const t = text.trim();
  // Estrategia 1: todo el texto es JSON
  if (t.startsWith("{")) {
    try { const p = JSON.parse(t); if (p.diagnostico_completo) return p; } catch (_) {}
  }
  // Estrategia 2: extraer con regex (el JSON puede estar en cualquier posición)
  const m = t.match(/\{[^{}]*"diagnostico_completo"[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/s);
  if (m) { try { const p = JSON.parse(m[0]); if (p.diagnostico_completo) return p; } catch (_) {} }
  // Estrategia 3: buscar desde primera { hasta última }
  const start = t.indexOf('{"diagnostico_completo"');
  if (start === -1) {
    const start2 = t.indexOf("{");
    const end = t.lastIndexOf("}");
    if (start2 !== -1 && end > start2) {
      try { const p = JSON.parse(t.slice(start2, end + 1)); if (p.diagnostico_completo) return p; } catch (_) {}
    }
  } else {
    const end = t.lastIndexOf("}");
    if (end > start) {
      try { const p = JSON.parse(t.slice(start, end + 1)); if (p.diagnostico_completo) return p; } catch (_) {}
    }
  }
  return null;
}

async function saveDiagnosticoToSupabase(parsed, userData) {
  if (!supabase) {
    console.warn("Supabase no está configurado (URL o ANON KEY faltante).");
    return null;
  }

  const payload = {
    nombre_contacto: userData.nombre || null,
    empresa: parsed.empresa || userData.empresa || null,
    email: userData.email || null,
    tipo_empresa: parsed.tipo || userData.tipo || null,
    score_global:
      typeof parsed.score_global === "number" ? parsed.score_global : null,
    nivel: parsed.nivel || null,
    resumen_ejecutivo: parsed.resumen_ejecutivo || null,
    payload: parsed,
  };

  const { data, error } = await supabase
    .from("diagnosticos")
    .insert([payload])
    .select("id")
    .single();

  if (error) {
    console.error("Error guardando diagnóstico en Supabase:", error);
    return null;
  }

  return data?.id || null;
}

function getLevel(score) {
  if (score < 20) return { color: P.red,   desc: "Su hotel no recuerda a nadie" };
  if (score < 40) return { color: P.gold,  desc: "La memoria existe y está dispersa" };
  if (score < 60) return { color: P.cyan,  desc: "Buen punto de partida" };
  if (score < 80) return { color: P.green, desc: "Posición competitiva sólida" };
  return             { color: P.blue,  desc: "Referente del sector" };
}

function RadarChart({ scores }) {
  const cx = 100, cy = 100, r = 72;
  const pts = DIMENSIONS.map((d, i) => {
    const a = (i / DIMENSIONS.length) * 2 * Math.PI - Math.PI / 2;
    const v = (scores[d.id] || 0) / 100;
    return { x: cx + r * v * Math.cos(a), y: cy + r * v * Math.sin(a),
             lx: cx + (r + 20) * Math.cos(a), ly: cy + (r + 20) * Math.sin(a),
             label: d.label.split(" ")[0], color: d.color };
  });
  return (
    <svg width="200" height="200" style={{ overflow: "visible" }}>
      {[0.25, 0.5, 0.75, 1].map((g, gi) => {
        const gp = DIMENSIONS.map((_, i) => {
          const a = (i / DIMENSIONS.length) * 2 * Math.PI - Math.PI / 2;
          return `${cx + r * g * Math.cos(a)},${cy + r * g * Math.sin(a)}`;
        });
        return <polygon key={gi} points={gp.join(" ")} fill="none" stroke={P.grayDark} strokeWidth="0.8" />;
      })}
      {DIMENSIONS.map((_, i) => {
        const a = (i / DIMENSIONS.length) * 2 * Math.PI - Math.PI / 2;
        return <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)} stroke={P.grayDark} strokeWidth="0.8" />;
      })}
      <polygon points={pts.map(p => `${p.x},${p.y}`).join(" ")} fill={P.blue + "28"} stroke={P.blue} strokeWidth="1.5" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3" fill={p.color} />
          <text x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: "8.5px", fill: P.grayLight, fontFamily: "monospace" }}>{p.label}</text>
        </g>
      ))}
    </svg>
  );
}

function ScoreBar({ score, color, label, animate }) {
  const [w, setW] = useState(0);
  useEffect(() => { if (animate) setTimeout(() => setW(score), 150); }, [animate, score]);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: P.grayLight, fontFamily: "monospace" }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color, fontFamily: "monospace" }}>{score}</span>
      </div>
      <div style={{ height: 4, background: P.grayDark, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 2,
          transition: "width 1.3s cubic-bezier(0.4,0,0.2,1)", boxShadow: `0 0 8px ${color}70` }} />
      </div>
    </div>
  );
}

const LOGO_URL = `${process.env.PUBLIC_URL || ""}/icon/icon.svg`;
const LOGO2_URL = `${process.env.PUBLIC_URL || ""}/icon/icon2.png`;
const NXLP_LINK = "https://www.nxlp.space";
const EXTERNADO_LINK = "https://www.uexternado.edu.co/";

function LogoLink() {
  return (
    <>
      {/* Logo nxlp.space - esquina superior izquierda */}
      <a
        href={NXLP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 1000,
          display: "inline-flex",
          textDecoration: "none",
        }}
        aria-label="Ir a nxlp.space"
      >
        <img
          src={LOGO_URL}
          alt="nxlp.space"
          style={{ height: 52, display: "block" }}
        />
      </a>
      {/* Logo Externado - esquina superior derecha */}
      <a
        href={EXTERNADO_LINK}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1000,
          display: "inline-flex",
          textDecoration: "none",
        }}
        aria-label="Ir a Universidad Externado de Colombia"
      >
        <img
          src={LOGO2_URL}
          alt="Universidad Externado de Colombia"
          style={{ height: 100, display: "block" }}
        />
      </a>
    </>
  );
}

export default function App() {
  const [phase, setPhase]         = useState("intro");
  const [userData, setUserData]   = useState({ nombre: "", empresa: "", email: "", tipo: "" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [animBars, setAnimBars]   = useState(false);
  const [userTurns, setUserTurns] = useState(0); // FIX 4: contador de respuestas
  const [diagnosticoId, setDiagnosticoId] = useState(null);
  const chatEnd = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { if (result) setTimeout(() => setAnimBars(true), 500); }, [result]);

  async function startChat() {
    if (!userData.nombre || !userData.empresa || !userData.tipo) return;
    setPhase("chat");
    setLoading(true);
    setUserTurns(0);
    const intro = `Hola, soy ${userData.nombre} de ${userData.empresa}, una ${EMPRESA_TIPOS.find(t => t.id === userData.tipo)?.label}. Quiero hacer el diagnóstico digital.`;
    const msgs = [{ role: "user", content: intro }];
    const reply = await callAI(msgs, SYSTEM_PROMPT);
    const parsed = parseResult(reply);
    if (parsed) {
      setResult(parsed);
      setPhase("result");
      const id = await saveDiagnosticoToSupabase(parsed, userData);
      setDiagnosticoId(id);
      setLoading(false);
      return;
    }
    setMessages([{ role: "user", content: intro }, { role: "assistant", content: reply }]);
    setLoading(false);
  }

  async function send(force = false) {
    const text = force
      ? "Ya respondí las 6 preguntas. Por favor genera ahora el JSON del diagnóstico completo sin texto adicional."
      : input.trim();
    if (!text || loading) return;
    const newMsgs = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    if (!force) setUserTurns(t => t + 1);
    const reply = await callAI(newMsgs, SYSTEM_PROMPT);
    const parsed = parseResult(reply);
    if (parsed) {
      setResult(parsed);
      setPhase("result");
      const id = await saveDiagnosticoToSupabase(parsed, userData);
      setDiagnosticoId(id);
      setLoading(false);
      return;
    }
    setMessages([...newMsgs, { role: "assistant", content: reply }]);
    setLoading(false);
  }

  async function downloadPDFReport() {
    if (!result || !resultRef.current) return;

    const element = resultRef.current;

    await new Promise(requestAnimationFrame);

    const toRestore = [];
    element.querySelectorAll("[data-no-pdf]").forEach((node) => {
      toRestore.push({ node, originalDisplay: node.style.display });
      node.style.display = "none";
    });

    let breakOffsetPx = null;
    const alertCards = element.querySelectorAll("[data-alert-card]");
    if (alertCards.length > 1) {
      const rectContainer = element.getBoundingClientRect();
      const rectSecond = alertCards[1].getBoundingClientRect();
      breakOffsetPx = rectSecond.top - rectContainer.top;
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        backgroundColor: P.bg,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const scaleByWidth = pdfWidth / canvas.width;
      const scaleByBreak =
        breakOffsetPx && breakOffsetPx > 0
          ? pdfHeight / breakOffsetPx
          : scaleByWidth;
      const s = Math.min(scaleByWidth, scaleByBreak);

      const imgWidth = canvas.width * s;
      const imgHeight = canvas.height * s;

      let heightLeft = imgHeight;
      let position = 0;

      // Fondo oscuro en cada página para evitar zonas blancas
      pdf.setFillColor(P.bg);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.setFillColor(P.bg);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }


      pdf.save(
        `diagnostico-${(result.empresa || "empresa")
          .replace(/\s+/g, "-")
          .toLowerCase()}.pdf`
      );
    } finally {
      toRestore.forEach(({ node, originalDisplay }) => {
        node.style.display = originalDisplay;
      });
    }
  }

  const gridBg = { position: "fixed", inset: 0, backgroundImage: `linear-gradient(${P.grayDark}18 1px,transparent 1px),linear-gradient(90deg,${P.grayDark}18 1px,transparent 1px)`, backgroundSize: "60px 60px", pointerEvents: "none" };

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === "intro") return (
    <div style={{ minHeight:"100vh", background:P.bg, fontFamily:"'Sora','Segoe UI',sans-serif", color:P.white, display:"flex", flexDirection:"column", alignItems:"center" }}>
      <LogoLink />
      <div style={gridBg} />
      <div style={{ position:"fixed", top:"-15%", left:"50%", transform:"translateX(-50%)", width:600, height:400, background:`radial-gradient(ellipse,${P.blue}14 0%,transparent 70%)`, pointerEvents:"none" }} />
      <div style={{ width:"100%", maxWidth:680, padding:"80px 24px 60px", textAlign:"center", position:"relative" }}>
        <div style={{ display:"inline-block", background:`${P.blue}15`, border:`1px solid ${P.blue}60`, color:P.blue, padding:"4px 16px", fontFamily:"monospace", fontSize:10, letterSpacing:3, marginBottom:32 }}>
          DIAGNÓSTICO DE MADUREZ DIGITAL · TURISMO
        </div>
        <h1 style={{ fontSize:48, fontWeight:700, lineHeight:1.1, marginBottom:16 }}>
          ¿Está tu empresa<br /><span style={{ color:P.blue }}>lista para la IA?</span>
        </h1>
        <p style={{ fontSize:15, color:P.gray, maxWidth:460, margin:"0 auto 48px", lineHeight:1.65 }}>
          En 3 minutos sabrá cuánto recuerda su hotel de sus huéspedes, qué se está perdiendo por no recordarlo y qué hacer el lunes.
        </p>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", marginBottom:52 }}>
          {DIMENSIONS.map(d => (
            <div key={d.id} style={{ background:`${d.color}12`, border:`1px solid ${d.color}40`, color:d.color, padding:"5px 14px", fontSize:11, fontFamily:"monospace" }}>
              {d.icon} {d.label}
            </div>
          ))}
        </div>
        <button onClick={() => setPhase("form")} style={{ background:P.blue, border:"none", color:P.white, padding:"14px 44px", fontSize:14, fontWeight:600, cursor:"pointer", letterSpacing:1, boxShadow:`0 0 32px ${P.blue}50`, fontFamily:"inherit" }}>
          INICIAR DIAGNÓSTICO →
        </button>
        <p style={{ marginTop:18, fontSize:11, color:P.gray, fontFamily:"monospace" }}>turismo.nxlp.space · Universidad Externado de Colombia</p>
      </div>
    </div>
  );

  // ── FORM ──────────────────────────────────────────────────────────────────
  const canStartDiagnostic = userData.nombre && userData.empresa && userData.tipo && privacyAccepted;
  if (phase === "form") return (
    <div style={{ minHeight:"100vh", background:P.bg, fontFamily:"'Sora','Segoe UI',sans-serif", color:P.white, display:"flex", flexDirection:"column", alignItems:"center" }}>
      <LogoLink />
      <div style={gridBg} />
      <div style={{ width:"100%", maxWidth:580, padding:"56px 24px 80px", position:"relative" }}>
        <button onClick={() => setPhase("intro")} style={{ background:"none", border:"none", color:P.gray, cursor:"pointer", fontSize:12, fontFamily:"monospace", marginBottom:28, padding:0 }}>← VOLVER</button>
        <div style={{ fontFamily:"monospace", fontSize:10, color:P.blue, letterSpacing:3, marginBottom:8 }}>IDENTIFICACIÓN</div>
        <h2 style={{ fontSize:28, fontWeight:700, marginBottom:8 }}>Cuéntanos sobre<br /><span style={{ color:P.blue }}>tu empresa</span></h2>
        <p style={{ color:P.gray, fontSize:14, marginBottom:36 }}>Esta información personaliza tu diagnóstico.</p>
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          {[
            { key:"nombre",  label:"Tu nombre",   ph:"María García" },
            { key:"empresa", label:"Nombre de la empresa", ph:"Viajes Colombia S.A.S." },
            { key:"email",   label:"Email (opcional)", ph:"maria@empresa.com" },
          ].map(f => (
            <div key={f.key}>
              <label style={{ display:"block", fontSize:11, fontFamily:"monospace", color:P.blue, letterSpacing:2, marginBottom:8 }}>{f.label.toUpperCase()}</label>
              <input value={userData[f.key]} onChange={e => setUserData(p=>({...p,[f.key]:e.target.value}))} placeholder={f.ph}
                style={{ width:"100%", padding:"12px 16px", background:P.bgCard, border:`1px solid ${P.grayDark}`, color:P.white, fontSize:14, outline:"none", fontFamily:"inherit" }}
                onFocus={e=>e.target.style.borderColor=P.blue} onBlur={e=>e.target.style.borderColor=P.grayDark} />
            </div>
          ))}
          <div>
            <label style={{ display:"block", fontSize:11, fontFamily:"monospace", color:P.blue, letterSpacing:2, marginBottom:8 }}>TIPO DE EMPRESA</label>
            <div style={{ display:"flex", gap:10 }}>
              {EMPRESA_TIPOS.map(t => (
                <button key={t.id} onClick={() => setUserData(p=>({...p,tipo:t.id}))}
                  style={{ flex:1, padding:"12px 6px", background:userData.tipo===t.id?`${P.blue}20`:P.bgCard, border:`1px solid ${userData.tipo===t.id?P.blue:P.grayDark}`, color:userData.tipo===t.id?P.blue:P.gray, fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <label style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer", marginTop:8 }}>
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={e => setPrivacyAccepted(e.target.checked)}
              style={{ marginTop: 4, width: 18, height: 18, accentColor: P.blue }}
            />
            <span style={{ fontSize: 13, color: P.grayLight, lineHeight: 1.5 }}>
              Acepto las <a href="https://www.nxlp.space" target="_blank" rel="noopener noreferrer" style={{ color: P.blue, textDecoration: "underline" }}>políticas de privacidad</a> y autorizo el tratamiento de mis datos para este diagnóstico.
            </span>
          </label>
        </div>
        <button onClick={startChat} disabled={!canStartDiagnostic}
          style={{ width:"100%", marginTop:32, padding:"15px", background:canStartDiagnostic?P.blue:P.grayDark, border:"none", color:P.white, fontSize:14, fontWeight:600, cursor:canStartDiagnostic?"pointer":"not-allowed", letterSpacing:1, fontFamily:"inherit" }}>
          COMENZAR DIAGNÓSTICO CON IA →
        </button>
      </div>
    </div>
  );

  // ── CHAT ──────────────────────────────────────────────────────────────────
  if (phase === "chat") return (
    <div style={{ minHeight:"100vh", background:P.bg, fontFamily:"'Sora','Segoe UI',sans-serif", color:P.white, display:"flex", flexDirection:"column" }}>
      <LogoLink />
      {/* Header */}
      <div style={{ background:`${P.bgCard}F2`, borderBottom:`1px solid ${P.grayDark}`, padding:"12px 24px", display:"flex", alignItems:"center", gap:10, position:"sticky", top:0, zIndex:10 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:P.green, boxShadow:`0 0 7px ${P.green}` }} />
        <span style={{ fontFamily:"monospace", fontSize:10, color:P.blue, letterSpacing:2 }}>IA CONSULTORA ACTIVA</span>
        <span style={{ marginLeft:"auto", fontSize:11, color:P.gray, fontFamily:"monospace" }}>{userData.empresa}</span>
      </div>

      {/* Messages */}
      <div style={{ flex:1, maxWidth:720, width:"100%", margin:"0 auto", padding:"24px 20px 150px" }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display:"flex", justifyContent:m.role==="user"?"flex-end":"flex-start", marginBottom:16 }}>
            {m.role==="assistant" && (
              <div style={{ width:28, height:28, background:P.blue, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, marginRight:10, flexShrink:0, alignSelf:"flex-start", marginTop:2 }}>◈</div>
            )}
            <div style={{ maxWidth:"78%", background:m.role==="user"?`${P.blue}1C`:P.bgCard, border:`1px solid ${m.role==="user"?`${P.blue}40`:P.grayDark}`, padding:"12px 16px", fontSize:14, lineHeight:1.65, color:P.grayLight }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <div style={{ width:28, height:28, background:P.blue, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>◈</div>
            <div style={{ background:P.bgCard, border:`1px solid ${P.grayDark}`, padding:"12px 18px", display:"flex", gap:6 }}>
              {[0,1,2].map(j=>(
                <div key={j} style={{ width:6, height:6, borderRadius:"50%", background:P.blue, opacity:0.7, animation:`pulse 1.2s ${j*0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={chatEnd} />
      </div>

      {/* FIX 4: botón de forzar diagnóstico visible desde turno 5 */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:`${P.bg}F8`, borderTop:`1px solid ${P.grayDark}`, padding:"12px 20px 18px" }}>
        {userTurns >= 5 && !loading && (
          <div style={{ maxWidth:720, margin:"0 auto 10px" }}>
            <button onClick={() => send(true)} style={{ width:"100%", padding:"9px", background:`${P.blue}15`, border:`1px dashed ${P.blue}55`, color:P.blue, fontSize:11, cursor:"pointer", fontFamily:"monospace", letterSpacing:1 }}>
              ✓ YA RESPONDÍ TODO — GENERAR DIAGNÓSTICO AHORA
            </button>
          </div>
        )}
        <div style={{ maxWidth:720, margin:"0 auto", display:"flex", gap:10 }}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()} placeholder="Escribe tu respuesta..." disabled={loading}
            style={{ flex:1, padding:"12px 16px", background:P.bgCard, border:`1px solid ${P.grayDark}`, color:P.white, fontSize:14, outline:"none", fontFamily:"inherit" }}
            onFocus={e=>e.target.style.borderColor=P.blue} onBlur={e=>e.target.style.borderColor=P.grayDark} />
          <button onClick={()=>send()} disabled={loading||!input.trim()} style={{ background:input.trim()&&!loading?P.blue:P.grayDark, border:"none", color:P.white, padding:"12px 22px", cursor:input.trim()&&!loading?"pointer":"not-allowed", fontSize:16 }}>→</button>
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{transform:scale(1);opacity:.5}50%{transform:scale(1.35);opacity:1}}`}</style>
    </div>
  );

  // ── RESULT ─────────────────────────────────────────────────────────────────
  if (phase === "result" && result) {
    const level = getLevel(result.score_global);
    const rc = { red:P.red, gold:P.gold, blue:P.blue, green:P.green };
    return (
      <div style={{ minHeight:"100vh", background:P.bg, fontFamily:"'Sora','Segoe UI',sans-serif", color:P.white, display:"flex", flexDirection:"column", alignItems:"center" }}>
        <LogoLink />
        <div style={gridBg} />
        <div style={{ position:"fixed", top:0, left:"50%", transform:"translateX(-50%)", width:800, height:300, background:`radial-gradient(ellipse,${level.color}10 0%,transparent 70%)`, pointerEvents:"none" }} />
        <div ref={resultRef} style={{ width:"100%", maxWidth:720, padding:"48px 24px 80px", position:"relative" }}>
          <div style={{ fontFamily:"monospace", fontSize:10, color:P.blue, letterSpacing:3, marginBottom:8 }}>DIAGNÓSTICO COMPLETADO</div>
          <h1 style={{ fontSize:30, fontWeight:700, marginBottom:4 }}>{result.empresa}</h1>
          <p style={{ color:P.gray, fontSize:13, marginBottom:32 }}>{EMPRESA_TIPOS.find(t=>t.id===userData.tipo)?.label} · {userData.nombre}</p>

          <div style={{ background:P.bgCard, border:`1px solid ${level.color}40`, padding:28, marginBottom:22, display:"flex", gap:28, alignItems:"center" }}>
            <div style={{ textAlign:"center", flexShrink:0 }}>
              <div style={{ fontSize:70, fontWeight:700, color:level.color, fontFamily:"monospace", lineHeight:1 }}>{result.score_global}</div>
              <div style={{ fontSize:17, fontWeight:600, color:P.white, marginTop:5 }}>{result.nivel}</div>
              <div style={{ fontSize:11, color:P.gray, marginTop:2 }}>{level.desc}</div>
            </div>
            <div style={{ flex:1, display:"flex", justifyContent:"center" }}><RadarChart scores={result.scores} /></div>
          </div>

          <div style={{ background:P.bgCard, border:`1px solid ${P.grayDark}`, padding:"20px 24px", marginBottom:20 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.blue, letterSpacing:3, marginBottom:14 }}>DIMENSIONES</div>
            {DIMENSIONS.map(d => <ScoreBar key={d.id} score={result.scores[d.id]} color={d.color} label={d.label} animate={animBars} />)}
          </div>

          <div style={{ background:P.bgCard, borderLeft:`3px solid ${P.blue}`, padding:"18px 22px", marginBottom:20 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.blue, letterSpacing:3, marginBottom:10 }}>RESUMEN EJECUTIVO</div>
            <p style={{ fontSize:14, lineHeight:1.7, color:P.grayLight }}>{result.resumen_ejecutivo}</p>
          </div>

          <div style={{ marginBottom:20 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.red, letterSpacing:3, marginBottom:12 }}>⚠ ALERTAS DE ACCIÓN INMEDIATA</div>
            {result.alertas_urgentes.map((a,i) => {
              const c = a.impacto==="Crítico"?P.red:P.gold;
              return (
                <div key={i} data-alert-card style={{ background:P.bgCard, border:`1px solid ${c}28`, padding:"14px 18px", marginBottom:10, display:"flex", gap:12 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:c, flexShrink:0, marginTop:5, boxShadow:`0 0 6px ${c}` }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontWeight:600, fontSize:14, color:P.white }}>{a.titulo}</span>
                      <span style={{ fontFamily:"monospace", fontSize:9, letterSpacing:2, padding:"2px 8px", background:`${c}15`, color:c, border:`1px solid ${c}40` }}>{a.impacto.toUpperCase()}</span>
                    </div>
                    <p style={{ fontSize:13, color:P.gray, lineHeight:1.5 }}>{a.descripcion}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ background:P.bgCard, border:`1px solid ${P.grayDark}`, padding:"18px 22px", marginBottom:20 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.green, letterSpacing:3, marginBottom:12 }}>FORTALEZAS IDENTIFICADAS</div>
            {result.fortalezas.map((f,i) => (
              <div key={i} style={{ display:"flex", gap:10, marginBottom:8, fontSize:13, color:P.grayLight }}>
                <span style={{ color:P.green }}>▸</span> {f}
              </div>
            ))}
          </div>

          <div style={{ marginBottom:20 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.cyan, letterSpacing:3, marginBottom:14 }}>ROADMAP DE TRANSFORMACIÓN DIGITAL</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {result.roadmap.map((fase,i) => {
                const c = rc[fase.color]||P.blue;
                return (
                  <div key={i} style={{ background:P.bgCard, border:`1px solid ${P.grayDark}`, borderTop:`2px solid ${c}`, padding:"16px 18px" }}>
                    <div style={{ fontSize:11, fontWeight:600, color:c, marginBottom:12, fontFamily:"monospace" }}>{fase.fase}</div>
                    {fase.acciones.map((a,j) => (
                      <div key={j} style={{ fontSize:12, color:P.gray, padding:"5px 0", borderBottom:`1px solid ${P.grayDark}`, display:"flex", gap:8 }}>
                        <span style={{ color:c }}>→</span> {a}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ background:`${P.purple}10`, border:`1px solid ${P.purple}40`, padding:"18px 22px", marginBottom:32 }}>
            <div style={{ fontFamily:"monospace", fontSize:10, color:P.purple, letterSpacing:3, marginBottom:10 }}>◈ RECOMENDACIÓN IA PRIORITARIA</div>
            <p style={{ fontSize:14, lineHeight:1.7, color:P.grayLight }}>{result.recomendacion_ia}</p>
          </div>

          <div style={{ display:"flex", gap:12 }}>
            <button
              data-no-pdf
              onClick={downloadPDFReport}
              style={{ flex:1, padding:"14px", background:P.blue, border:"none", color:P.white, fontSize:13, fontWeight:600, cursor:"pointer", letterSpacing:1, fontFamily:"inherit", boxShadow:`0 0 20px ${P.blue}40` }}
            >
              ↓ DESCARGAR PDF
            </button>
            <button
              data-no-pdf
              onClick={() => { setPhase("intro"); setMessages([]); setResult(null); setUserTurns(0); setUserData({nombre:"",empresa:"",email:"",tipo:""}); setPrivacyAccepted(false); }}
              style={{ padding:"14px 20px", background:"none", border:`1px solid ${P.grayDark}`, color:P.gray, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
            >
              Nuevo diagnóstico
            </button>
          </div>
          <p style={{ textAlign:"center", marginTop:22, fontSize:11, color:P.gray, fontFamily:"monospace" }}>
            turismo.nxlp.space · @miguelmar99 · Universidad Externado de Colombia
          </p>
        </div>
      </div>
    );
  }
  return null;
}
